import { combineEpics, ofType } from 'redux-observable';
import { map, switchMap } from 'rxjs/operators';

import { rs } from 'core/api';
import { reduxSwitch } from 'core/redux/tools';
import { ActionGeneratorBuilder, errorMap } from 'core/redux/actions';

const NEW_OFFICER_ID = 'new-officer';

export const actions = new ActionGeneratorBuilder('veta-entry-passes-officers')
	.subtype('load', (load) => load.request().success('data').fail().cancel())
	.subtype('add', (add) => add.request({ phone: true, remarks: true }).success().fail().cancel())
	.subtype('update', (update) => update.request({ id: true, data: true }).success({ id: true, data: true }).fail())
	.subtype('remove', (remove) => remove.request({ id: true }).success().fail())
	.type('edit', { id: true })
	.type('intent', { intent: true })
	.type('select', { officers: true })
	.type('cancel')
	.build();

const defaultState = {
	intent: null,
	selectedId: null,
	selected: [],
	data: null,
	request: { isLoading: false, error: null },
};

const onIntent = (state, { intent }) => {
	if (intent === 'add') {
		const newOfficer = { id: NEW_OFFICER_ID, phone: '', remarks: '' };

		return {
			...state,
			intent,
			selectedId: NEW_OFFICER_ID,
			data: intent === 'add' ? [...state.data, newOfficer] : state.data,
		};
	}

	return { ...state, intent };
};

const onLoadSuccess = (state, action) => {
	const officers = { data: action.data, request: { isLoading: false, error: null } };

	if (state.intent === 'add') {
		return { ...state, ...officers, intent: null, selectedId: null };
	}

	return { ...state, ...officers };
};

const onUpdateSuccess = (state, { id, data }) => {
	const officers = state.data.map((officer) => (officer.id === id ? { ...officer, ...data } : officer));

	return { ...state, intent: null, selectedId: null, data: officers, request: { isLoading: false, error: null } };
};

const leasesReducer = (state, action) => {
	switch (action.type) {
		case actions.load.request.type:
		case actions.add.request.type:
		case actions.update.request.type:
		case actions.remove.request.type:
			return { ...state, request: { isLoading: true, error: null } };
		case actions.load.success.type:
			return onLoadSuccess(state, action);
		case actions.update.success.type:
			return onUpdateSuccess(state, action);
		case actions.load.fail.type:
		case actions.add.fail.type:
		case actions.update.fail.type:
		case actions.remove.fail.type:
			return { ...state, request: { isLoading: false, error: action.errorMessage } };
		case actions.edit.type:
			return { ...state, intent: 'edit', selectedId: action.id };
		case actions.intent.type:
			return onIntent(state, action);
		case actions.select.type:
			return { ...state, selected: action.officers };
		case actions.cancel.type:
			return {
				...state,
				intent: null,
				selectedId: null,
				selected: [],
				data: state.data.filter(({ id }) => id !== NEW_OFFICER_ID),
				request: { isLoading: false, error: null }
			};
		default:
			return state;
	}
};

export const reducer = reduxSwitch([leasesReducer], defaultState);

const loadEpic = (action$) => {
	return action$.pipe(
		ofType(actions.load.request.type),
		switchMap((action) =>
			rs('veta/entry-passes/officers')
				.defer$()
				.pipe(
					map((data) => actions.load.success({ data })),
					errorMap(actions.load.fail),
				),
		),
	);
};

const addEpic = (action$) => {
	return action$.pipe(
		ofType(actions.add.request.type),
		switchMap((action) =>
			rs('veta/entry-passes/officers')
				.post({ phone: action.phone, remarks: action.remarks || '' })
				.defer$()
				.pipe(
					map(() => actions.load.request()),
					errorMap(actions.add.fail),
				),
		),
	);
};

const updateEpic = (action$, state$) => {
	return action$.pipe(
		ofType(actions.update.request.type),
		switchMap((action) =>
			rs(`veta/entry-passes/officers/${action.id}`)
				.put(action.data)
				.defer$()
				.pipe(
					map(() => actions.update.success({ id: action.id, data: action.data })),
					errorMap(actions.update.fail),
				),
		),
	);
};

const removeEpic = (action$, state$) => {
	return action$.pipe(
		ofType(actions.remove.request.type),
		switchMap((action) =>
			rs(`veta/entry-passes/officers/${action.id}`)
				.delete()
				.defer$()
				.pipe(
					map(() => actions.load.request()),
					errorMap(actions.remove.fail),
				),
		),
	);
};

export const epic = combineEpics(loadEpic, addEpic, updateEpic, removeEpic);
